import React, { useRef, useState } from "react";
import { X, ChevronRight, AlertCircle, Loader2 } from "lucide-react";
import type { Swiper as SwiperType } from "swiper";
import { getExchangeConfig } from "@/utils/exchange-config";
import { useSetupExchange } from "@/hooks/useExchanges";
import { useUserProxyIp } from "@/hooks/useProxy";
import { toast } from "sonner";
import { getTradeErrorMessage } from "@/utils/trade-error";
import { ExchangeTutorialContent } from "./ExchangeTutorialContent";
import { Dialog } from "./Dialog";

interface ExchangeSetupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exchangeCode: string;
  exchangeName: string;
  userId: string;
}

type TabType = "tutorial" | "credentials";

/**
 * Desktop dialog version of ExchangeSetupDrawer.
 * Displays as a centered modal instead of a bottom sheet.
 */
export const ExchangeSetupDialog: React.FC<ExchangeSetupDialogProps> = ({
  open,
  onOpenChange,
  exchangeCode,
  exchangeName,
  userId,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>("tutorial");
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [apiPassphrase, setApiPassphrase] = useState("");
  const [setupError, setSetupError] = useState<string | null>(null);
  const [copiedIp, setCopiedIp] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  const isHyperliquid = exchangeCode === "hyperliquid";

  const apiKeyFieldRef = useRef<HTMLInputElement>(null);
  const apiSecretFieldRef = useRef<HTMLInputElement>(null);
  const apiPassphraseFieldRef = useRef<HTMLInputElement>(null);

  const setupMutation = useSetupExchange();
  const { data: userProxyIp } = useUserProxyIp(userId);
  const config = getExchangeConfig(exchangeCode, userProxyIp || undefined);

  // Restore slide position when switching back to tutorial tab
  React.useEffect(() => {
    if (activeTab === "tutorial" && swiperInstance && currentSlideIndex > 0) {
      swiperInstance.slideTo(currentSlideIndex, 0);
    }
  }, [activeTab, swiperInstance, currentSlideIndex]);

  const handleCopyIp = async () => {
    if (!config?.whitelistIp) return;

    try {
      // Try modern clipboard API first
      if (navigator.clipboard && globalThis.isSecureContext) {
        await navigator.clipboard.writeText(config.whitelistIp);
      } else {
        // Fallback to execCommand for older browsers or non-secure contexts
        const textArea = document.createElement("textarea");
        textArea.value = config.whitelistIp;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        // eslint-disable-next-line deprecation/deprecation
        const successful = document.execCommand("copy");
        textArea.remove();
        
        if (!successful) {
          throw new Error("Copy command failed");
        }
      }
      
      setCopiedIp(true);
      toast.success("IP address copied to clipboard");
      setTimeout(() => setCopiedIp(false), 2000);
    } catch (error) {
      console.error("Failed to copy IP address:", error);
      toast.error("Failed to copy IP address. Please copy manually.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!apiKey.trim()) {
      apiKeyFieldRef.current?.focus();
      toast.error(
        isHyperliquid
          ? "Please fill in the API Wallet Public Key"
          : "Please fill in the API Key"
      );
      return;
    }

    if (!apiSecret.trim()) {
      apiSecretFieldRef.current?.focus();
      toast.error(
        isHyperliquid
          ? "Please fill in the API Wallet Private Key"
          : "Please fill in the API Secret"
      );
      return;
    }

    if (config?.requiresPassphrase && !apiPassphrase.trim()) {
      apiPassphraseFieldRef.current?.focus();
      toast.error(
        isHyperliquid
          ? "Please fill in the Account Wallet"
          : "Please fill in the API Passphrase"
      );
      return;
    }

    setSetupError(null);

    try {
      await setupMutation.mutateAsync({
        userId,
        exchangeCode,
        apiKey: apiKey.trim(),
        apiSecret: apiSecret.trim(),
        apiPassphrase: apiPassphrase.trim(),
      });

      toast.success(`${exchangeName} connected successfully!`);
      resetAndClose();
    } catch (error) {
      const errorMessage = getTradeErrorMessage(
        error,
        "Failed to setup exchange"
      );
      setSetupError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const resetAndClose = () => {
    setApiKey("");
    setApiSecret("");
    setApiPassphrase("");
    setSetupError(null);
    setActiveTab("tutorial");
    setCurrentSlideIndex(0);
    setSwiperInstance(null);
    onOpenChange(false);
  };

  const handleClose = () => {
    resetAndClose();
  };

  if (!config) {
    return null;
  }

  const customHeader = (
    <div className="flex-shrink-0 border-b border-border">
      <div className="flex items-center justify-between p-4">
        <h2 className="text-xl font-bold text-text-primary">
          Connect {exchangeName}
        </h2>
        <button
          onClick={handleClose}
          className="p-2 hover:bg-primary rounded-lg transition-colors"
        >
          <X className="w-5 h-5 text-text-secondary" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border">
        <button
          onClick={() => setActiveTab("tutorial")}
          className={`flex-1 px-4 py-3 font-medium transition-colors ${
            activeTab === "tutorial"
              ? "text-accent border-b-2 border-accent"
              : "text-text-secondary hover:text-text-primary"
          }`}
        >
          Tutorial
        </button>
        <button
          onClick={() => setActiveTab("credentials")}
          className={`flex-1 px-4 py-3 font-medium transition-colors ${
            activeTab === "credentials"
              ? "text-accent border-b-2 border-accent"
              : "text-text-secondary hover:text-text-primary"
          }`}
        >
          API Credentials
        </button>
      </div>
    </div>
  );

  const footer =
    activeTab === "tutorial" ? (
      <button
        onClick={() => setActiveTab("credentials")}
        className="w-full bg-accent hover:bg-accent-hover text-text-primary py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
      >
        Continue to API Setup
        <ChevronRight className="w-5 h-5" />
      </button>
    ) : undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      customHeader={customHeader}
      footer={footer}
      maxWidth="2xl"
      showCloseButton={false}
      contentClassName="p-6"
    >
      {activeTab === "tutorial" ? (
        <ExchangeTutorialContent
          config={config}
          exchangeCode={exchangeCode}
          exchangeName={exchangeName}
          copiedIp={copiedIp}
          currentSlideIndex={currentSlideIndex}
          onCopyIp={handleCopyIp}
          onSwiperInit={setSwiperInstance}
          onSlideChange={(swiper) => setCurrentSlideIndex(swiper.activeIndex)}
          imageContainerRef={imageContainerRef}
          maxImageHeight="350px"
        />
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label
              htmlFor="api-key-input"
              className="block text-sm font-medium text-text-primary"
            >
              {isHyperliquid ? "API Wallet Public Key" : "API Key"}
            </label>
            <input
              ref={apiKeyFieldRef}
              id="api-key-input"
              type="text"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={
                isHyperliquid
                  ? "Enter your API Wallet public key (0x...)"
                  : "Enter your API Key"
              }
              className="w-full bg-primary border border-border rounded-lg px-4 py-3 text-text-primary placeholder-text-secondary focus:outline-none focus:border-accent transition-colors"
              disabled={setupMutation.isPending}
            />
          </div>

          <div className="space-y-2">
            <label
              htmlFor="api-secret-input"
              className="block text-sm font-medium text-text-primary"
            >
              {isHyperliquid ? "API Wallet Private Key" : "API Secret"}
            </label>
            <input
              ref={apiSecretFieldRef}
              id="api-secret-input"
              type="password"
              value={apiSecret}
              onChange={(e) => setApiSecret(e.target.value)}
              placeholder={
                isHyperliquid
                  ? "Enter your API Wallet private key"
                  : "Enter your API Secret"
              }
              className="w-full bg-primary border border-border rounded-lg px-4 py-3 text-text-primary placeholder-text-secondary focus:outline-none focus:border-accent transition-colors"
              disabled={setupMutation.isPending}
            />
            {!isHyperliquid && (
              <p className="text-xs text-text-secondary">
                Note: The API Secret is different from your API Key
              </p>
            )}
          </div>

          {config.requiresPassphrase && (
            <div className="space-y-2">
              <label
                htmlFor="api-passphrase-input"
                className="block text-sm font-medium text-text-primary"
              >
                {isHyperliquid ? "Account Wallet" : "API Passphrase"}
              </label>
              <input
                ref={apiPassphraseFieldRef}
                id="api-passphrase-input"
                type="password"
                value={apiPassphrase}
                onChange={(e) => setApiPassphrase(e.target.value)}
                placeholder={
                  isHyperliquid
                    ? "Enter your Account Wallet address (0x...)"
                    : "Enter your API Passphrase"
                }
                className="w-full bg-primary border border-border rounded-lg px-4 py-3 text-text-primary placeholder-text-secondary focus:outline-none focus:border-accent transition-colors"
                disabled={setupMutation.isPending}
              />
              {isHyperliquid && (
                <p className="text-xs text-text-secondary">
                  This is the Ethereum wallet address you used to create your
                  Hyperliquid account
                </p>
              )}
            </div>
          )}

          {setupError && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-red-500 mb-1">
                    Setup Failed
                  </h4>
                  <p className="text-sm text-text-secondary">{setupError}</p>
                </div>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={setupMutation.isPending}
            className="w-full bg-accent hover:bg-accent-hover text-text-primary py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {setupMutation.isPending ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Connecting...
              </>
            ) : (
              "Connect Exchange"
            )}
          </button>
        </form>
      )}
    </Dialog>
  );
};
